"use client";

import type { Document as ContentfulDocument } from "@contentful/rich-text-types";
import { TypeCtaSkeleton, TypePopUpModalSkeleton } from "@contentful/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { sendGTMEvent } from "@next/third-parties/google";
import { GtmEvents } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@yardzen/ui/components/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@yardzen/ui/components/form";
import { H1 } from "@yardzen/ui/components/h1";
import { Icon } from "@yardzen/ui/components/icon";
import { Input } from "@yardzen/ui/components/input";
import { Spinner } from "@yardzen/ui/components/spinner";
import { VisuallyHidden } from "@yardzen/ui/components/visually-hidden";
import { Entry } from "contentful";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { RichText } from "../../../../next-components/src/contentful/rich-text/RichText";
import { isCustomerLeadEmailNew } from "../../../email-capture-form/src/lib/actions";
import { createCustomerLeadFromModal } from "./createCustomerLead";

function setCookie(name: string, value: string, expirationDays?: number): void {
  let cookieString = `${name}=${value};path=/`;
  if (expirationDays !== undefined) {
    const expirationDate = new Date();
    expirationDate.setTime(
      expirationDate.getTime() + expirationDays * 24 * 60 * 60 * 1000,
    );
    const expires = `expires=${expirationDate.toUTCString()}`;
    cookieString = `${cookieString};${expires}`;
  }
  // If expirationDays is undefined, cookie will be a session cookie (no expiration)
  document.cookie = cookieString;
}

function shouldShowModal(
  currentPath: string,
  restrictedPaths: string[] | undefined,
): boolean {
  // Check if current path is in restricted paths
  if (restrictedPaths && restrictedPaths.length > 0) {
    const isPathRestricted = restrictedPaths.some((restrictedPath) => {
      // Handle exact matches and path prefixes
      return (
        currentPath === restrictedPath || currentPath.startsWith(restrictedPath)
      );
    });
    if (isPathRestricted) {
      return false;
    }
  }

  // Check for 'yz_modal_dismiss' cookie that is set on modal dismissal, will not show for 7 days
  if (
    document.cookie
      .split(";")
      .some((cookie) => cookie.trim().startsWith("yz_modal_dismiss="))
  ) {
    return false;
  }

  // Check for 'yz-design-profile-completed' cookie, will not show modal if set to 'true'
  const designProfileCookie = document.cookie
    .split(";")
    .find((cookie) => cookie.trim().startsWith("yz-design-profile-completed="));
  if (designProfileCookie) {
    const cookieValue = designProfileCookie.split("=")[1]?.trim();
    if (cookieValue === "true") {
      return false;
    }
  }

  return true;
}

export default function LeadCaptureModal({
  contentfulContent,
}: {
  contentfulContent: Entry<
    TypePopUpModalSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  >;
}) {
  const {
    heading,
    isVisible,
    image,
    delayBeforeOpening,
    modalBody,
    primaryCta,
    alternateCta,
    alternateBody,
    primarySuccessRta,
    successModalBody,
    primaryDisclaimer,
    alternateDisclaimer,
    restrictedPaths,
  } = contentfulContent?.fields;
  if (!isVisible) return null;
  const path = usePathname();
  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [formVariant, setFormVariant] = useState<"email" | "sms">("email");
  const [smsSubmitted, setSmsSubmitted] = useState(false);

  useEffect(() => {
    setRootElement(document.getElementById("root"));
  }, []);

  useEffect(() => {
    if (rootElement && shouldShowModal(path, restrictedPaths)) {
      const timer = setTimeout(
        () => {
          setOpen(true);
        },
        delayBeforeOpening ? delayBeforeOpening * 1000 : 5000,
      );

      return () => clearTimeout(timer);
    }
  }, [rootElement, path, restrictedPaths]);

  if (!rootElement) {
    return null;
  }

  // Early return if modal should not be shown (avoids rendering Dialog unnecessarily)
  if (!shouldShowModal(path, restrictedPaths)) {
    return null;
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    // Set cookie when modal is dismissed via X button or backdrop click (7 days expiration)
    // Don't set it if we're on the success screen (that has its own handler)
    if (!isOpen && !smsSubmitted) {
      setCookie("yz_modal_dismiss", "true", 7);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <VisuallyHidden>
        <DialogTitle>Let's Keep in Touch</DialogTitle>
      </VisuallyHidden>
      <DialogContent className="bg-brand-beige-lighter flex w-full max-w-5xl flex-col !gap-0 !rounded-xl !border-none !p-0 md:flex-row">
        <div className=" h-[250px] md:h-full md:flex-[1_1_50%]">
          <Image
            src={`https:${image?.fields.file?.url}`}
            alt={heading ?? ""}
            className="h-full w-full rounded-t-xl object-cover [object-position:center_calc(50%-75px)] md:rounded-l-xl md:[object-position:center]"
            width={888}
            height={888}
          />
        </div>
        <div className="flex flex-[1_1_80%] flex-col justify-center px-4 py-4 pb-8 md:flex-[1_1_50%] md:px-16 md:py-0 md:pb-0">
          <div className="flex flex-col">
            <H1>{heading}</H1>
            {smsSubmitted ? (
              <ModalSuccess
                successModalBody={successModalBody}
                cta={primarySuccessRta}
                onDismiss={() => {
                  setOpen(false);
                  // Set cookie when success modal button is clicked (no expiration)
                  setCookie("yz_modal_dismiss", "true");
                }}
              />
            ) : (
              <>
                {formVariant === "email" ? (
                  <EmailInput
                    onSuccess={() => setFormVariant("sms")}
                    primaryDisclaimer={primaryDisclaimer}
                    modalBody={modalBody}
                    primaryCta={primaryCta}
                  />
                ) : (
                  <SMSInput
                    onSuccess={() => setSmsSubmitted(true)}
                    alternateDisclaimer={alternateDisclaimer}
                    modalBody={alternateBody}
                    alternateCta={alternateCta}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmailInput({
  onSuccess,
  primaryDisclaimer,
  modalBody,
  primaryCta,
}: {
  onSuccess: () => void;
  primaryDisclaimer: ContentfulDocument | undefined;
  modalBody: ContentfulDocument | undefined;
  primaryCta:
    | Entry<TypeCtaSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>
    | undefined;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const leadCaptureSchema = z.object({
    email: z.string().email("Invalid email address"),
  });

  const form = useForm({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(leadCaptureSchema),
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Validate form first
    const isValid = await form.trigger();
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      const email = form.getValues("email");
      const isNewLead = await isCustomerLeadEmailNew(email);
      const formData = new FormData();
      formData.append("email", email);
      await createCustomerLeadFromModal(null, formData);

      sendGTMEvent({
        event: GtmEvents.GENERATE_LEAD,
        new_design_quiz_lead: isNewLead ? 1 : 0,
      });

      setIsSubmitting(false);
      // Switch to SMS input on successful submission
      onSuccess();
    } catch (error) {
      console.error("Failed to submit form:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {modalBody && (
        <RichText
          content={modalBody}
          pClassName="!mb-0 py-4 md:pb-8 md:pt-6 !md:text-lg !text-base !leading-normal"
        />
      )}
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col gap-0 md:gap-3"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Enter your email"
                    type="email"
                    {...field}
                    className="w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="w-full py-4">
            <Button
              type="submit"
              className="w-full"
              disabled={!form.formState.isValid || isSubmitting}
            >
              {isSubmitting ? (
                <Spinner className="size-4" />
              ) : (
                primaryCta?.fields.buttonText || "Submit Email"
              )}
              <Icon className="ml-2 h-4 w-4 text-white" name="arrow-right" />
            </Button>
          </div>
        </form>
      </Form>
      {primaryDisclaimer && (
        <RichText
          content={primaryDisclaimer}
          pClassName="md:!text-xs !text-xxs text-brand-moss-greens-90 !my-0 md:!my-6"
        />
      )}
    </div>
  );
}

function SMSInput({
  onSuccess,
  alternateDisclaimer,
  modalBody,
  alternateCta,
}: {
  onSuccess?: () => void;
  alternateDisclaimer: ContentfulDocument | undefined;
  modalBody: ContentfulDocument | undefined;
  alternateCta:
    | Entry<TypeCtaSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>
    | undefined;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const phoneCaptureSchema = z.object({
    phone: z
      .string()
      .min(1, "Phone number is required")
      .regex(/^[\d\s\-\(\)]+$/, "Invalid phone number format")
      .refine(
        (phone) => phone.replace(/\D/g, "").length >= 10,
        "Phone number must contain at least 10 digits",
      ),
  });

  const form = useForm({
    defaultValues: {
      phone: "",
    },
    resolver: zodResolver(phoneCaptureSchema),
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Validate form first
    const isValid = await form.trigger();
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("phone", form.getValues("phone"));
      await createCustomerLeadFromModal(null, formData);
      setIsSubmitting(false);
      // Call onSuccess callback if provided (e.g., to close modal)
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Failed to submit phone number:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {modalBody && (
        <RichText
          content={modalBody}
          className="py-2 md:py-6"
          pClassName="!mb-0 !md:text-lg !text-base !leading-normal !mt-0"
        />
      )}
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col gap-0 md:gap-3"
        >
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => {
              const { ref: _, ...fieldProps } = field;
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      ref={(e) => {
                        field.ref(e);
                        inputRef.current = e;
                      }}
                      placeholder="Enter your phone number"
                      type="tel"
                      {...fieldProps}
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <div className="w-full py-4">
            <Button
              type="submit"
              className="w-full"
              disabled={!form.formState.isValid || isSubmitting}
            >
              {isSubmitting ? (
                <Spinner className="size-4" />
              ) : (
                alternateCta?.fields.buttonText || "Submit Phone Number"
              )}{" "}
              <Icon className="ml-2 h-4 w-4 text-white" name="arrow-right" />
            </Button>
          </div>
        </form>
      </Form>
      {alternateDisclaimer && (
        <RichText
          content={alternateDisclaimer}
          pClassName="md:text-xs !text-xxs text-brand-moss-greens-90 !my-0"
        />
      )}
    </div>
  );
}

function ModalSuccess({
  successModalBody,
  cta,
  onDismiss,
}: {
  successModalBody: ContentfulDocument | undefined;
  cta: Entry<TypeCtaSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string> | undefined;
  onDismiss: () => void;
}) {
  const router = useRouter();

  const handleButtonClick = () => {
    // default URL just in case the cta is not set
    const url = cta?.fields.url || "https://yardzen.com/gallery";
    const openInNewTab = cta?.fields.openInNewTab;
    // Dismiss modal and set cookie
    onDismiss();
    if (openInNewTab) {
      window.open(url, "_blank");
    } else if (url.startsWith("http://") || url.startsWith("https://")) {
      window.location.href = url;
    } else {
      router.push(url);
    }
  };

  return (
    <div className="w-full py-4">
      <Icon name="circle-check" size="lg" />
      {successModalBody && (
        <RichText
          content={successModalBody}
          className="!my-4"
          pClassName="!md:text-lg !text-base !mt-0 !mb-0 !leading-normal"
        />
      )}
      {cta && (
        <div className="w-full py-4">
          <Button onClick={handleButtonClick} className="w-full">
            {cta.fields.buttonText || "Browse Inspiration"}
            <Icon className="ml-2 h-4 w-4 text-white" name="arrow-right" />
          </Button>
        </div>
      )}
    </div>
  );
}
